import TeamsColumn from '@/modules/teams/TeamsColumn'
import { useReadTemplates } from '@/modules/templates/hooks/useReadTemplates'
import { VLoaderAnimationFullSizeCentered } from '@/ui-components/VLoader/VLoader'
import { cn } from '@/utils/Cn'
import {
  ColumnDef,
  PaginationState,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  EngagementScoreSummary,
  ScoreDescriptor,
  SpaceListColumn,
  SpaceListColumnSettings,
  TSpace,
  getCompanyInitials,
  mapScoreDescriptorToLabel,
  mapScoreToDescriptor,
} from '@valuecase/common'
import {
  AvatarThumbnail,
  Button,
  CheckMark,
  DataTable,
  Highlight,
  IonIcon,
  ModalControls,
  OneLineTruncatedText,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useDateFormatting,
  useNotifications,
  useOverlay,
  usePagination,
} from '@valuecase/ui-components'
import { Button2 } from '@valuecase/ui-components/src/components/ui/button'
import { Progress } from '@valuecase/ui-components/src/components/ui/progress'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { copyToClipboard } from '../../../../utils/ClipboardUtils'
import { ReadSpacesListDTO } from '../../hooks/types'

import { getSubWithoutPrefix, useAuthState } from '@/auth/auth'
import { useHasSomePermission } from '@/auth/permissions'
import { useNavigate } from '@tanstack/react-router'
import { Badge, BadgeVariants } from '@valuecase/ui-components/src/components/ui/badge'
import { DateTime } from 'luxon'
import { useLDflags } from '../../../../launchdarkly/Launchdarkly'
import { useTrackEvent } from '../../../../mixpanel/useTrackEvent'
import SpaceDeleteModal from '../../../../ui-components/SpaceDeleteModal'
import TemplateFromSpaceCreatorModal from '../../../templates/TemplateFromSpaceCreator/TemplateFromSpaceCreatorModal'
import { useSpaceListView } from '../../context/SpaceListViewContext'
import { useChangeSpacedArchivedStatus } from '../../hooks/useChangeSpacedArchivedStatus'
import { useDeleteSpace } from '../../hooks/useDeleteSpace'
import { SpaceOwner, useReadAvailableSpaceOwners } from '../../hooks/useReadAvailableSpaceOwners'
import { SpaceQuerySortBy, useSpacesQuery } from '../../hooks/useReadSpaces'
import { useSpacesSearch } from '../../hooks/useSpacesSearch'
import NoSpacesFound from '../NoSpacesFound'
import { SpaceContextMenu } from '../SpaceContextMenu'
import SpacesPageHeader from '../spaceModuleHeader/SpacesPageHeader'
import { useSpaceSheet } from '../spaceSheet/SpaceSheetContext'
import SpaceBatchActionWrapper from './batch-actions/SpaceBatchActionWrapper'
import EditSpaceTeamsDialog from './dialogs/EditSpaceTeamsDialog'
import { SpaceFilter, useSpacesFilterReset } from './SpaceFilter'
import SpacesViews from './SpacesViews'

const DEFAULT_PAGE_SIZE = 10
interface Props {
  spaces: (ReadSpacesListDTO & {
    ownerName: string
    ownerEmail?: string
    picture?: string
    teams?: {
      id: string
      name: string
    }[]
  })[]
  availableSpaceOwners: SpaceOwner[]
  pageIndex: number
  pageSize: number
  totalPages: number
  isLoading: boolean
  setPagination: (pagination: PaginationState) => void
}

type MAPStatus = ReadSpacesListDTO['actionPlanSummary']['status']

export const mapActionPlanStatusToStyleVariant = (
  status: MAPStatus,
): Extract<BadgeVariants, 'neutral' | 'attention' | 'info' | 'danger' | 'success'> => {
  switch (status) {
    case 'notCreated':
      return 'neutral'
    case 'notStarted':
      return 'neutral'
    case 'stalled':
      return 'attention'
    case 'inProgress':
      return 'info'
    case 'overdue':
      return 'danger'
    case 'completed':
      return 'success'
    default:
      throw new Error(`unknown action plan status: ${status}`)
  }
}

export const mapActionPlanStatusToLabel = (status: MAPStatus): string => {
  switch (status) {
    case 'notCreated':
      return '-'
    case 'notStarted':
      return 'Not started'
    case 'stalled':
      return 'Stalled'
    case 'inProgress':
      return 'In progress'
    case 'overdue':
      return 'Overdue'
    case 'completed':
      return 'Completed'
    default:
      throw new Error(`unknown action plan status: ${status}`)
  }
}

export type SpacesListDto = Props['spaces'][number]

const ProfileImage = ({ src }: { src: string }) => (
  <img
    src={src}
    className='inline-block min-h-6 min-w-6 w-6 h-6 rounded-full border border-grey-s2 box-border'
  />
)

const OwnerCell = ({ children }: { children: React.ReactNode }) => (
  <div className='flex items-center gap-3 text-grey-s6 text-xs leading-4 whitespace-nowrap'>
    {children}
  </div>
)

const EngagementScoreCell = ({
  engagementSummary,
  activityCountInLast14Days,
  spaceLinkToActivityFeed,
}: {
  engagementSummary: EngagementScoreSummary
  activityCountInLast14Days: number
  spaceLinkToActivityFeed: string
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false)

  const { score, scoreBreakdown, inputs } = engagementSummary

  const scoreDescriptor = useMemo(
    () => mapScoreToDescriptor(score, activityCountInLast14Days),
    [score, activityCountInLast14Days],
  )

  const scoreDescriptorLabel = useMemo(
    () => mapScoreDescriptorToLabel(scoreDescriptor),
    [scoreDescriptor],
  )

  const scoreIcon = useCallback((score: ScoreDescriptor, className?: string) => {
    switch (score) {
      case 'high':
        return (
          <IonIcon
            name={'c_engagement-high'}
            className={cn('w-4 h-4 text-success-s4', className)}
          />
        )

      case 'medium':
        return (
          <IonIcon
            name={'c_engagement-medium'}
            className={cn('w-4 h-4 text-[#FDBA74]', className)}
          />
        )
      case 'low':
        return (
          <IonIcon name={'c_engagement-low'} className={cn('w-4 h-4 text-warning-s4', className)} />
        )
    }
  }, [])

  const sessionCount = useMemo(() => inputs?.sessionCount ?? 0, [inputs?.sessionCount])

  const activitiesPerVisit = useMemo(
    () => Math.floor((inputs?.nonSessionActivityCount ?? 0) / (inputs?.sessionCount || 1)),
    [inputs?.nonSessionActivityCount, inputs?.sessionCount],
  )

  const nonSessionOrViewActivityCount = useMemo(
    () => inputs?.nonSessionOrViewActivityCount ?? 0,
    [inputs?.nonSessionOrViewActivityCount],
  )

  const stakeholdersCount = useMemo(
    () => inputs?.activityStakeholders ?? 0,
    [inputs?.activityStakeholders],
  )

  if (score === undefined || scoreDescriptor === 'n/a') {
    return <div className={'flex items-center '}>-</div>
  }

  return (
    // use modal here to prevent clicking on the row itself when open
    <Popover modal open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <div
          className={
            'flex gap-2 items-center w-full h-full justify-center hover:bg-grey-s2 p-2 rounded-lg transition-colors'
          }
        >
          {scoreIcon(scoreDescriptor)}
          {scoreDescriptorLabel}
        </div>
      </PopoverTrigger>
      <PopoverContent
        onClick={(e) => {
          // prevents the row click to open the space itself
          e.stopPropagation()
        }}
      >
        <div className={'flex flex-col gap-4'}>
          <div className={'flex gap-2 mx-2'}>
            {scoreIcon(scoreDescriptor, 'w-8 h-8')}
            <div className={'flex flex-col gap-1'}>
              <h3 className={'font-bold'}>{scoreDescriptorLabel} engagement</h3>
              <p className={'text-xs text-grey-s5'}>in the last 14 days</p>
            </div>
          </div>

          <div className={'flex justify-between items-center mx-2'}>
            <p className={'text-sm'}>
              {sessionCount} {sessionCount === 1 ? 'visit' : 'visits'}
            </p>
            {scoreIcon(mapScoreToDescriptor(scoreBreakdown.sessionScore))}
          </div>

          <div className={'flex justify-between items-center mx-2'}>
            <p className={'text-sm'}>
              {activitiesPerVisit} {activitiesPerVisit === 1 ? 'activity' : 'activities'} per visit
            </p>
            {scoreIcon(mapScoreToDescriptor(scoreBreakdown.activitiesPerVisitScore))}
          </div>

          <div className={'flex justify-between items-center mx-2'}>
            <p className={'text-sm'}>
              {nonSessionOrViewActivityCount} non page-view{' '}
              {nonSessionOrViewActivityCount === 1 ? 'interaction' : 'interactions'}
            </p>
            {scoreIcon(mapScoreToDescriptor(scoreBreakdown.activitiesScore))}
          </div>

          <div className={'flex justify-between items-center mx-2'}>
            <p className={'text-sm'}>
              {stakeholdersCount} unique active{' '}
              {stakeholdersCount === 1 ? 'stakeholder' : 'stakeholders'}
            </p>
            {scoreIcon(mapScoreToDescriptor(scoreBreakdown.stakeholdersScore))}
          </div>

          <Button
            label={'Go to Space Activity'}
            style={'secondary'}
            fullWidth
            size={'small'}
            iconPosition={'right'}
            icon={'arrow-forward'}
            onClick={() => {
              window.location.href = spaceLinkToActivityFeed
            }}
          />
          <a
            href={'https://help.valuecase.de/en/articles/136387-how-our-engagement-score-works'}
            target={'_blank'}
            onClick={(e) => e.stopPropagation()}
            className={
              'flex gap-1 text-grey-s4 hover:text-grey-s5 transition-colors text-xs font-semibold items-center w-fit'
            }
            rel='noreferrer'
          >
            <IonIcon name={'help-circle-outline'} className={'w-3 h-3 text-grey-s5'} />
            Learn more
          </a>
        </div>
      </PopoverContent>
    </Popover>
  )
}

const SpacesList = () => {
  const { success, error } = useNotifications()
  const { trackEvent } = useTrackEvent()
  const { formatDateWithRelativeWords } = useDateFormatting()
  const { flags, isFetched: isFlagsFetched } = useLDflags()
  const overlay = useOverlay()
  const modalControls = useRef<ModalControls | null>(null)
  const { deleteSpaceAsync } = useDeleteSpace()
  const [spaceSelectedForDeletion, selectSpaceForDeletion] = useState<SpacesListDto>()
  const [showWaitingForLoadAnimation, setShowWaitingForLoadAnimation] = useState(false)
  const [showDeleteSpaceModal, setShowDeleteSpaceModal] = useState(false)
  const { changeSpaceArchivedStatus } = useChangeSpacedArchivedStatus()
  const { availableSpaceOwners } = useReadAvailableSpaceOwners()
  const [spaceSelectedForTeamEditing, setSpaceSelectedForTeamEditing] =
    useState<SpacesListDto | null>(null)
  const {
    selectedView,
    localViewSettings,
    isLoading: isLoadingViews,
    placeholderView,
  } = useSpaceListView()
  const navigate = useNavigate()

  const showCreateTemplateFromSpaceOverlay = useCallback(
    (spaceId: string) => {
      overlay.show(<TemplateFromSpaceCreatorModal controlsRef={modalControls} spaceId={spaceId} />)
    },
    [overlay],
  )
  const onArchiveClick = useCallback(
    async (spaceId: string) => {
      try {
        await changeSpaceArchivedStatus(spaceId, true)
        success('Space successfully archived')
      } catch (err) {
        error('There was an error archiving the space. Please try again later.')
      }
    },
    [changeSpaceArchivedStatus, success, error],
  )

  const { setPagination, resetPagination } = usePagination()
  const { unarchivedTemplates: templates } = useReadTemplates()

  const { filters } = useSpacesSearch()
  const { spaceName } = filters

  const condensed = useMemo(() => {
    return Boolean(filters.pageSize && filters.pageSize > 10)
  }, [filters])

  const [searchValue, setSearchValue] = useState(spaceName || '')

  useEffect(() => {
    setSearchValue(spaceName || '')
  }, [spaceName])

  const { resetFilters } = useSpacesFilterReset({
    searchValue,
    setSearchValue,
  })

  const [sorting, setSorting] = useState<SortingState>(() => {
    if (filters.sortBy && filters.sortDirection) {
      return [{ id: filters.sortBy, desc: filters.sortDirection === 'desc' }]
    }
    return [{ id: 'createdAt', desc: true }]
  })

  useEffect(() => {
    if (filters.sortBy && filters.sortDirection) {
      setSorting([{ id: filters.sortBy, desc: filters.sortDirection === 'desc' }])
    }
  }, [filters.sortBy, filters.sortDirection])

  const {
    spaces: spaceData,
    isLoading,
    isFetching,
    totalPages,
    totalCount,
    totalCountWithFilters,
  } = useSpacesQuery({
    filters: {
      ...filters,
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? DEFAULT_PAGE_SIZE,
      sortBy: filters.sortBy,
      sortDirection: filters.sortDirection,
    },
    includeSpaceScore: true,
    includeActionPlanKeyDetails: true,
    enabled: isFlagsFetched,
  })

  const getIdWithoutPrefix = useMemo(
    () => (id: string) => {
      return id.replace(/^bubble\|/, '')
    },
    [],
  )

  const spaces = useMemo(() => {
    return spaceData?.map((space) => {
      const owner = availableSpaceOwners?.find(
        (owner) => getIdWithoutPrefix(owner.id) === space.ownerId,
      )

      let ownerName = ''
      if (owner?.firstName && owner?.lastName) {
        ownerName = `${owner.firstName} ${owner.lastName}`
      } else if (owner?.lastName) {
        ownerName = owner.lastName
      } else if (owner?.firstName) {
        ownerName = owner.firstName
      }
      return {
        ...space,
        ownerName,
        ownerEmail: owner?.email,
        picture: owner?.picture,
      }
    })
  }, [getIdWithoutPrefix, spaceData, availableSpaceOwners])

  const getUrlWithToken = useCallback(
    (spaceId: string, token?: string, showActivityFeed?: boolean): string => {
      const url = new URL(location.href)

      url.pathname = `/spaces/${spaceId}`
      url.search = ''

      if (token) {
        url.searchParams.set('token', token)
      }

      if (showActivityFeed) {
        url.searchParams.set('openActivityFeed', 'true')
      }

      return url.href
    },
    [],
  )

  const onCopyLinkToSpace = useMemo(
    () => (id: string, token?: string) => {
      const redirectTo = getUrlWithToken(id, token)
      copyToClipboard(redirectTo)
      success('Link to space copied to your clipboard')
    },
    [getUrlWithToken, success],
  )

  const { openSpaceSheet, selectedSpaceId } = useSpaceSheet()

  const [selectedSpaces, setSelectedSpaces] = useState<Set<string>>(new Set())

  const handleSelectAll = useCallback(() => {
    if (selectedSpaces.size === spaces?.length) {
      setSelectedSpaces(new Set())
    } else {
      setSelectedSpaces(new Set(spaces?.map((space) => space.rootNodeId) || []))
    }
  }, [spaces, selectedSpaces.size])

  const handleSelectSpace = useCallback(
    (spaceId: string) => {
      const newSelected = new Set(selectedSpaces)
      if (newSelected.has(spaceId)) {
        newSelected.delete(spaceId)
      } else {
        newSelected.add(spaceId)
      }
      setSelectedSpaces(newSelected)
    },
    [selectedSpaces],
  )

  const auth = useAuthState()
  const hasPermissionToChangeSpaceOwner = useHasSomePermission(
    'USERS_MANAGE',
    'SPACES_ALL_ADMINISTRATION',
  )

  const isAllowedToManageSpace = useCallback(
    (space: Props['spaces'][number]) => {
      return hasPermissionToChangeSpaceOwner || space.ownerId === getSubWithoutPrefix(auth)
    },
    [hasPermissionToChangeSpaceOwner, auth],
  )

  const columns = useMemo<ColumnDef<Props['spaces'][number]>[]>(() => {
    const columns: ColumnDef<Props['spaces'][number]>[] = [
      {
        id: 'companyName',
        accessorKey: 'companyName',
        header: () => (
          <div className='flex items-center gap-4 w-fit max-w-[300px]'>
            {flags && flags['new-data-table'] && (
              <div
                className='cursor-pointer'
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                <CheckMark
                  checked={spaces?.length ? selectedSpaces.size === spaces.length : false}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSelectAll()
                  }}
                />
              </div>
            )}
            <span>Company Name</span>
          </div>
        ),
        meta: {
          sticky: true,
        },
        enableHiding: false,
        cell: (ctx) => {
          const { companyLogo, companyName, archived, parentTemplateName } = ctx.row.original
          const initials = getCompanyInitials(companyName)?.toLowerCase()
          const canManage = isAllowedToManageSpace(ctx.row.original)

          return (
            <div className='flex items-center gap-4 group relative max-w-[300px]'>
              {flags && flags['new-data-table'] && (
                <div
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <CheckMark
                          checked={selectedSpaces.has(ctx.row.original.rootNodeId)}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (!canManage) {
                              return
                            }
                            handleSelectSpace(ctx.row.original.rootNodeId)
                          }}
                          disabled={!canManage}
                        />
                      </div>
                    </TooltipTrigger>
                    {!canManage && (
                      <TooltipContent>Disabled - you can&apos;t manage this Space.</TooltipContent>
                    )}
                  </Tooltip>
                </div>
              )}
              <div className='flex items-center gap-3'>
                {archived ? (
                  <AvatarThumbnail type='icon' icon='archive' size={'MEDIUM'} />
                ) : companyLogo ? (
                  <AvatarThumbnail type='image' imageUrl={companyLogo} size={'MEDIUM'} />
                ) : (
                  <AvatarThumbnail type='gravatarInitials' initials={initials} size={'MEDIUM'} />
                )}
                <div className='flex flex-col'>
                  <span className='text-sm font-semibold'>
                    <OneLineTruncatedText>
                      <Highlight searchTerm={spaceName ?? ''}>{companyName}</Highlight>
                    </OneLineTruncatedText>
                  </span>
                  <span className='text-grey-s5 text-xs'>
                    <OneLineTruncatedText>{parentTemplateName}</OneLineTruncatedText>
                  </span>
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='absolute right-1 top-1/2 -translate-y-1/2'>
                    <Button2
                      variant={'subtle'}
                      size={'x-small'}
                      leadingIcon={'c_paragraph'}
                      onClick={(e) => {
                        e.stopPropagation()
                        trackEvent({
                          event: 'spaces-drawer-toggle',
                          eventProperties: {
                            indexPageName: 'Space List',
                            spaceId: ctx.row.original?.id || 'unknownSpaceId',
                          },
                        })

                        openSpaceSheet(ctx.row.original.rootNodeId)
                      }}
                      className={cn(
                        'opacity-0 group-hover:opacity-100 transition-opacity',
                        selectedSpaceId === ctx.row.original.rootNodeId && 'opacity-100',
                      )}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>Open Space details</TooltipContent>
              </Tooltip>
            </div>
          )
        },
      },
    ]

    const scoreColumnDefinition: ColumnDef<Props['spaces'][number]> = {
      id: 'score',
      header: 'Engagement Score',
      accessorKey: 'engagementSummary.score',
      enableSorting: true,
      cell: ({
        row: {
          original: { engagementSummary, rootNodeId },
        },
      }) => {
        return (
          <EngagementScoreCell
            engagementSummary={engagementSummary}
            activityCountInLast14Days={engagementSummary?.inputs?.allActivityCount ?? 0}
            spaceLinkToActivityFeed={getUrlWithToken(rootNodeId, undefined, true)}
          />
        )
      },
    }
    columns.push(scoreColumnDefinition)
    columns.push(
      {
        id: 'templateName',
        enableHiding: true,
        header: 'Template',
        cell: ({
          row: {
            original: { parentTemplateName },
          },
        }) => {
          return (
            <OneLineTruncatedText>{parentTemplateName || 'Deleted Template'}</OneLineTruncatedText>
          )
        },
      },
      {
        id: 'mapStatus',
        header: 'Plan Status',
        meta: { columnFilterLabel: 'Status' },
        accessorKey: 'actionPlanSummary.status',
        enableSorting: false,
        cell: ({
          row: {
            original: {
              actionPlanSummary: { status },
            },
          },
        }) => {
          return (
            <Tooltip>
              <TooltipTrigger>
                {status === 'notCreated' ? (
                  '-'
                ) : (
                  <Badge variant={mapActionPlanStatusToStyleVariant(status)}>
                    {mapActionPlanStatusToLabel(status)}
                  </Badge>
                )}
              </TooltipTrigger>
              {status === 'stalled' && (
                <TooltipContent>No task completed in the last 2 weeks.</TooltipContent>
              )}
            </Tooltip>
          )
        },
      },
      {
        id: 'mapProgress',
        header: 'Plan Progress',
        meta: { columnFilterLabel: 'Progress' },
        accessorKey: 'actionPlanSummary.progress',
        cell: ({
          row: {
            original: {
              actionPlanSummary: { status, progress },
            },
          },
        }) => {
          if (status === 'notCreated') {
            return '-'
          }
          return (
            <Progress
              className='flex-col items-start'
              variant={mapActionPlanStatusToStyleVariant(status)}
              value={progress * 100}
            />
          )
        },
      },
    )
    columns.push(
      {
        id: 'currentMilestone',
        enableHiding: true,
        enableSorting: false,
        header: () => (
          <div className='whitespace-nowrap overflow-hidden text-ellipsis max-w-[180px]'>
            Current Milestone
          </div>
        ),
        accessorKey: 'actionPlanKeyDetails.currentMilestone',
        cell: ({
          row: {
            original: { actionPlanKeyDetails },
          },
        }) => {
          return (
            <div className='max-w-[180px]'>
              <OneLineTruncatedText>
                {actionPlanKeyDetails?.currentMilestone || '-'}
              </OneLineTruncatedText>
            </div>
          )
        },
      },
      {
        id: 'lastCompletedMilestone',
        enableHiding: true,
        enableSorting: false,
        header: () => (
          <div className='whitespace-nowrap overflow-hidden text-ellipsis'>
            Last Completed Milestone
          </div>
        ),
        accessorKey: 'actionPlanKeyDetails.lastCompletedMilestone',
        cell: ({
          row: {
            original: { actionPlanKeyDetails },
          },
        }) => {
          return (
            <div className='max-w-[180px]'>
              <OneLineTruncatedText>
                {actionPlanKeyDetails?.mostRecentlyCompletedMilestone || '-'}
              </OneLineTruncatedText>
            </div>
          )
        },
      },
      {
        id: 'currentOpenTask',
        enableHiding: true,
        enableSorting: false,
        header: () => (
          <div className='whitespace-nowrap overflow-hidden text-ellipsis'>Current Open Task</div>
        ),
        accessorKey: 'actionPlanKeyDetails.currentOpenTask',
        cell: ({
          row: {
            original: { actionPlanKeyDetails },
          },
        }) => {
          return (
            <div className='max-w-[180px]'>
              <OneLineTruncatedText>
                {actionPlanKeyDetails?.currentOpenTask || '-'}
              </OneLineTruncatedText>
            </div>
          )
        },
      },
      {
        id: 'currentOpenTaskScheduled',
        enableHiding: true,
        enableSorting: false,
        header: () => (
          <div className='whitespace-nowrap overflow-hidden text-ellipsis'>Task Scheduled</div>
        ),
        accessorKey: 'actionPlanKeyDetails.currentOpenTaskScheduled',
        cell: ({
          row: {
            original: { actionPlanKeyDetails },
          },
        }) => {
          return (
            <div className='max-w-[180px]'>
              <OneLineTruncatedText>
                {actionPlanKeyDetails?.currentOpenTask === null
                  ? '-'
                  : actionPlanKeyDetails?.currentOpenTaskIsScheduled
                    ? 'Yes'
                    : 'No'}
              </OneLineTruncatedText>
            </div>
          )
        },
      },
      {
        id: 'nextDueDate',
        enableHiding: true,
        enableSorting: false,
        header: () => (
          <div className='whitespace-nowrap overflow-hidden text-ellipsis'>Next Due Date</div>
        ),
        accessorKey: 'actionPlanKeyDetails.nextDueDate',
        cell: ({
          row: {
            original: { actionPlanKeyDetails },
          },
        }) => {
          return (
            <div className='max-w-[180px] capitalize'>
              <OneLineTruncatedText>
                {actionPlanKeyDetails?.nextDueDate
                  ? formatDateWithRelativeWords(
                      DateTime.fromISO(actionPlanKeyDetails.nextDueDate as unknown as string, {
                        zone: 'utc',
                      }),
                      'short',
                    )
                  : '-'}
              </OneLineTruncatedText>
            </div>
          )
        },
      },
      {
        id: 'lastCompletedTaskDate',
        enableHiding: true,
        enableSorting: false,
        header: () => (
          <div className='whitespace-nowrap overflow-hidden text-ellipsis'>
            Last Completed Task Date
          </div>
        ),
        accessorKey: 'actionPlanKeyDetails.lastCompletedTaskDate',
        cell: ({
          row: {
            original: { actionPlanKeyDetails },
          },
        }) => {
          return (
            <div className='max-w-[180px] capitalize'>
              <OneLineTruncatedText>
                {actionPlanKeyDetails?.mostRecentlyCompletedTaskDate
                  ? formatDateWithRelativeWords(
                      DateTime.fromISO(
                        actionPlanKeyDetails.mostRecentlyCompletedTaskDate as unknown as string,
                        { zone: 'utc' },
                      ),
                      'short',
                    )
                  : '-'}
              </OneLineTruncatedText>
            </div>
          )
        },
      },
      {
        id: 'lastCompletedTask',
        enableHiding: true,
        enableSorting: false,
        header: () => (
          <div className='whitespace-nowrap overflow-hidden text-ellipsis'>Last Completed Task</div>
        ),
        accessorKey: 'actionPlanKeyDetails.lastCompletedTask',
        cell: ({
          row: {
            original: { actionPlanKeyDetails },
          },
        }) => {
          return (
            <div className='max-w-[180px]'>
              <OneLineTruncatedText>
                {actionPlanKeyDetails?.mostRecentlyCompletedTask || '-'}
              </OneLineTruncatedText>
            </div>
          )
        },
      },
    )
    columns.push(
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: 'Created At',
        cell: (ctx) => (
          <span className='capitalize'>
            {formatDateWithRelativeWords(ctx.renderValue() as string, 'short')}
          </span>
        ),
      },
      {
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        header: 'Last Changed',
        cell: (ctx) => (
          <span className='capitalize'>
            {formatDateWithRelativeWords(ctx.renderValue() as string, 'short')}
          </span>
        ),
      },
      {
        id: 'ownerId',
        accessorKey: 'ownerId',
        header: 'Owner',
        cell: (ctx) => (
          <div className='flex items-center gap-2  max-w-[180px]'>
            {ctx.row.original.picture && <ProfileImage src={ctx.row.original.picture} />}

            <OneLineTruncatedText>{ctx.row.original.ownerName}</OneLineTruncatedText>
          </div>
        ),
        minSize: 150,
      },
      {
        id: 'lastVisit',
        accessorKey: 'lastVisit',
        header: 'Last Visit',
        cell: (ctx) => {
          if (!ctx.row.original.visits || ctx.row.original.visits === 0) {
            return '-'
          }
          const renderedValue = ctx.renderValue()
          if (
            !renderedValue ||
            typeof renderedValue !== 'string' ||
            isNaN(Date.parse(renderedValue))
          ) {
            return 'More than 14 days ago'
          }

          return (
            <span className='capitalize'>
              {formatDateWithRelativeWords(renderedValue, 'short')}
            </span>
          )
        },
      },
      { id: 'visits', accessorKey: 'visits', header: 'Visits', maxSize: 70 },
      {
        id: 'activitiesCount',
        accessorKey: 'activitiesCount',
        header: 'Activities',
      },
    )
    if (flags && flags['teams-management']) {
      const teamsColumnDefinition: ColumnDef<Props['spaces'][number]> = {
        id: 'teams',
        header: 'Teams',
        cell: (ctx) => <TeamsColumn teams={ctx.row.original.teams} />,
        enableSorting: true,
        enableHiding: true,
      }
      columns.push(teamsColumnDefinition)
    }
    columns.push({
      id: 'actions',
      enableHiding: false,
      cell: (ctx) => {
        return (
          <div className={'flex justify-center items-center pr-6 gap-2'}>
            <Button2
              variant={'plain'}
              size={'small'}
              leadingIcon={'share-outline'}
              onClick={(e) => {
                e.stopPropagation()
                trackEvent({
                  event: 'spaces-copy_link-share_button',
                  eventProperties: {
                    indexPageName: 'Space List',
                  },
                })
                onCopyLinkToSpace(ctx.row.original.rootNodeId, ctx.row.original.authToken)
              }}
            />
            <SpaceContextMenu
              space={ctx.row.original as unknown as TSpace}
              onCopyLinkToSpace={onCopyLinkToSpace}
              showCreateTemplateFromSpaceOverlay={showCreateTemplateFromSpaceOverlay}
              setSpaceSelectedForTeamEditing={setSpaceSelectedForTeamEditing}
              selectSpaceForDeletion={selectSpaceForDeletion}
              setShowDeleteSpaceModal={setShowDeleteSpaceModal}
              getUrlWithToken={getUrlWithToken}
            />
          </div>
        )
      },
    })

    if (!selectedView || !localViewSettings || isLoadingViews) {
      return columns
    }

    // Get the current view settings, preferring local settings if they exist
    const currentSettings = selectedView
      ? localViewSettings[selectedView.id] || selectedView.settings
      : placeholderView?.settings

    // Filter and sort columns based on view settings
    const filteredColumns = columns.filter((column) => {
      const id = column.id
      // Always show companyName and actions, otherwise check view settings
      if (id === 'companyName' || id === 'actions') return true
      return (
        column.enableHiding === false ||
        (currentSettings?.visible as Record<SpaceListColumn, boolean>)[id as SpaceListColumn]
      )
    })

    // Sort columns ensuring companyName is first and actions is last
    return filteredColumns.sort((a, b) => {
      if (a.id === 'select') return -1
      if (b.id === 'select') return 1
      if (a.id === 'companyName') return -1
      if (b.id === 'companyName') return 1
      if (a.id === 'actions') return 1
      if (b.id === 'actions') return -1

      const orderA = (currentSettings?.order as SpaceListColumn[]).indexOf(a.id as SpaceListColumn)
      const orderB = (currentSettings?.order as SpaceListColumn[]).indexOf(b.id as SpaceListColumn)
      // If column is not in order array, put it at the end
      if (orderA === -1) return 1
      if (orderB === -1) return -1
      return orderA - orderB
    })
  }, [
    flags,
    selectedView,
    localViewSettings,
    isLoadingViews,
    placeholderView?.settings,
    spaceName,
    selectedSpaceId,
    trackEvent,
    openSpaceSheet,
    getUrlWithToken,
    formatDateWithRelativeWords,
    onCopyLinkToSpace,
    showCreateTemplateFromSpaceOverlay,
    selectedSpaces,
    handleSelectSpace,
    isAllowedToManageSpace,
    handleSelectAll,
    spaces?.length,
  ])

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    const currentSettings = selectedView
      ? localViewSettings[selectedView.id] || selectedView.settings
      : undefined
    return currentSettings?.visible as VisibilityState
  })

  useEffect(() => {
    const currentSettings = selectedView
      ? localViewSettings[selectedView.id] || selectedView.settings
      : undefined
    setColumnVisibility(currentSettings?.visible as VisibilityState)
  }, [selectedView, localViewSettings])

  useEffect(() => {
    resetPagination()
  }, [filters, sorting, resetPagination])

  // Filter out selected spaces that are no longer present in the fetched data
  useEffect(() => {
    if (spaces) {
      const availableSpaceIds = new Set(spaces.map((space) => space.rootNodeId))
      const newSelectedSpaces = new Set(
        Array.from(selectedSpaces).filter((id) => availableSpaceIds.has(id)),
      )
      if (newSelectedSpaces.size !== selectedSpaces.size) {
        setSelectedSpaces(newSelectedSpaces)
      }
    }
  }, [spaces, selectedSpaces])

  const pagination = useMemo(
    () => ({
      pageIndex: (filters.page ?? 1) - 1,
      pageSize: filters.pageSize ?? DEFAULT_PAGE_SIZE,
    }),
    [filters],
  )

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPagination({ pageIndex: 0, pageSize: newPageSize })
      trackEvent({
        event: 'spaces-change_page_size',
        eventProperties: {
          indexPageName: 'Space List',
          pageSize: newPageSize,
        },
      })

      // Update URL with new page size
      navigate({
        to: '/spaces',
        search: (prev) => ({
          ...prev,
          pageSize: newPageSize,
          page: 1, // Reset to first page when changing page size
        }),
      })
    },
    [setPagination, trackEvent, navigate],
  )

  const table = useReactTable({
    data: spaces ?? [],
    columns: columns as any,
    enableFilters: true,
    onSortingChange: (newSorting) => {
      trackEvent({
        event: 'spaces-sort',
        eventProperties: {
          indexPageName: 'Space List',
        },
      })

      setSorting(newSorting)

      // Update URL with sorting parameters
      if (typeof newSorting === 'function') {
        const currentSort = newSorting(sorting)[0]
        if (currentSort) {
          navigate({
            to: '/spaces',
            search: (prev) => ({
              ...prev,
              sortBy: currentSort.id as SpaceQuerySortBy,
              sortDirection: currentSort.desc ? 'desc' : 'asc',
            }),
          })
        }
      } else if (newSorting.length > 0) {
        const currentSort = newSorting[0]

        navigate({
          to: '/spaces',
          search: (prev) => ({
            ...prev,
            sortBy: currentSort.id as SpaceQuerySortBy,
            sortDirection: currentSort.desc ? 'desc' : 'asc',
          }),
        })
      }
    },
    onColumnVisibilityChange: (state) => {
      if (typeof state === 'function' && selectedView) {
        const newVisibility = state(columnVisibility)
        const newSettings = {
          ...selectedView.settings,
          visible: newVisibility as Record<SpaceListColumn, boolean>,
        } as SpaceListColumnSettings
        setColumnVisibility(newVisibility)
      }
    },
    onPaginationChange: (state) => {
      if (typeof state === 'function') {
        const newState = state(pagination)
        setPagination(newState)

        // Update URL with pagination parameters
        navigate({
          to: '/spaces',
          search: (prev) => ({
            ...prev,
            page: newState.pageIndex + 1, // Convert to 1-based index for API
            pageSize: newState.pageSize,
          }),
        })
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    state: {
      sorting,
      columnVisibility,
      pagination,
    },
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
  })

  const shouldShowNoSpacesFound = useMemo(() => {
    return !isFetching && !isLoading && (!spaces || spaces.length === 0) && totalCount === 0
  }, [spaces, isLoading, isFetching, totalCount])

  // Column width map
  const columnWidthMap = useMemo(
    () => ({
      companyName: 300,
      score: 150,
      templateName: 180,
      mapStatus: 150,
      mapProgress: 150,
      currentMilestone: 180,
      lastCompletedMilestone: 200,
      currentOpenTask: 180,
      currentOpenTaskScheduled: 180,
      nextDueDate: 180,
      lastCompletedTaskDate: 200,
      lastCompletedTask: 180,
      createdAt: 110,
      updatedAt: 120,
      ownerId: 150,
      lastVisit: 110,
      visits: 70,
      activitiesCount: 90,
      teams: 130,
      actions: 100,
    }),
    [],
  )

  // Get current view settings, preferring local settings if they exist
  const currentViewSettings = useMemo(() => {
    if (!selectedView) return null
    return localViewSettings[selectedView.id] || selectedView.settings
  }, [selectedView, localViewSettings])

  // Get visible columns in correct order
  const visibleColumns = useMemo(() => {
    if (!currentViewSettings) return []

    // Always include companyName first and actions last
    const orderedColumns = ['companyName', ...currentViewSettings.order, 'actions']

    // Filter based on visibility settings
    return orderedColumns.filter((col) => {
      if (col === 'companyName' || col === 'actions') return true
      return currentViewSettings.visible[col as SpaceListColumn]
    })
  }, [currentViewSettings])

  if (isLoading && spaces?.length === 0) {
    return <VLoaderAnimationFullSizeCentered />
  }

  return (
    <>
      <SpacesPageHeader isSpaceListEmpty={shouldShowNoSpacesFound} />

      {shouldShowNoSpacesFound ? (
        <>
          <NoSpacesFound hasTemplates={templates && templates.length > 0} />
        </>
      ) : (
        <>
          {/* Lines below handle spaceSelectedForDeletion being undefined for type safety; however,
      showDeleteSpaceModal is only true when spaceSelectedForDeletion is a space and therefore,
      in practice, modal is only shown to users when a spaceSelectedForDeletion is set. Modal
      must be present here the whole time for animations to work (behind the scenes, actual html
      elements are only added to the dom tree when ready to be animated in). */}
          <SpaceDeleteModal
            isOpen={showDeleteSpaceModal}
            onOpenChange={setShowDeleteSpaceModal}
            spaceName={spaceSelectedForDeletion?.companyName ?? ''}
            isArchived={spaceSelectedForDeletion?.archived ?? false}
            isDeleting={showWaitingForLoadAnimation}
            onConfirm={async () => {
              if (!spaceSelectedForDeletion) {
                return
              }
              try {
                setShowWaitingForLoadAnimation(true)
                await deleteSpaceAsync(spaceSelectedForDeletion.rootNodeId)
                trackEvent({
                  event: 'spaces-delete-confirm',
                  eventProperties: {
                    indexPageName: 'Delete Space Modal',
                    spaceId: spaceSelectedForDeletion.id || 'unknownSpaceId',
                  },
                })
                success(`Space '${spaceSelectedForDeletion.companyName}' successfully deleted.`)
                setShowDeleteSpaceModal(false)
              } catch (err) {
                error('There was an error deleting the space. Please try again later.')
              } finally {
                setShowWaitingForLoadAnimation(false)
              }
            }}
            onArchiveClick={async () => {
              if (!spaceSelectedForDeletion) {
                return
              }
              try {
                setShowWaitingForLoadAnimation(true)
                await onArchiveClick(spaceSelectedForDeletion.rootNodeId)
                setShowDeleteSpaceModal(false)
              } catch (err) {
                error('There was an error archiving the space. Please try again later.')
              } finally {
                setShowWaitingForLoadAnimation(false)
              }
            }}
          />
          <SpacesViews />
          <SpaceFilter table={table} searchValue={searchValue} setSearchValue={setSearchValue} />
          {spaceSelectedForTeamEditing && (
            <EditSpaceTeamsDialog
              space={spaceSelectedForTeamEditing}
              onOpenChange={() => setSpaceSelectedForTeamEditing(null)}
            />
          )}

          <div className='relative flex-1 min-h-0'>
            <DataTable
              isLoading={isLoading}
              table={table}
              className={'pb-1'}
              condensed={condensed ?? true}
              activeRowId={selectedSpaceId as string | undefined}
              rowClickHandler={(row) => {
                trackEvent({
                  event: 'spaces-open-current_tab',
                  eventProperties: {
                    indexPageName: 'Space List',
                  },
                })
                window.location.href = getUrlWithToken(row.original.rootNodeId)
              }}
              rowClassNames={(row) => {
                return row.original.archived ? 'opacity-60' : undefined
              }}
              paginationClickHandler={() =>
                trackEvent({
                  event: 'spaces-change_page',
                  eventProperties: {
                    indexPageName: 'Space List',
                  },
                })
              }
              resetFiltersClickHandler={resetFilters}
              tableWrapperClassName={'rounded-lg border border-grey-s2'}
              totalLabel={`Total spaces: ${totalCountWithFilters ?? 0}`}
              onPageSizeChange={handlePageSizeChange}
              pageSize={filters.pageSize ?? DEFAULT_PAGE_SIZE}
              actionBar={
                <SpaceBatchActionWrapper
                  selectedSpaces={selectedSpaces}
                  spaces={spaces ?? []}
                  onSelectionChange={setSelectedSpaces}
                />
              }
              columnWidthMap={columnWidthMap}
              visibleColumns={visibleColumns}
            />
          </div>
        </>
      )}
    </>
  )
}

export default SpacesList
